import { IResponseField } from './IResponseField';
import { IBaseArgument } from './IBaseArgument';
import { generateRandomHash } from './GraphHelpers';
import { generateVariableParams } from './QueryVariablesHelper';

export abstract class BaseResponseBuilder {
    protected fields: {[key: string]: IResponseField} = {};
    protected hashCode: string = '';
    protected typeName: string = '';

    constructor(typeName: string) {
        this.typeName = typeName;
    }

    protected initField = (responseBuilder: BaseResponseBuilder  | null, args: IBaseArgument[]): IResponseField => {
        this.hashCode = generateRandomHash();
        return responseBuilder ? { responseBuilder, arguments: args } : {};
    }

    protected addVariable = (name: string, graphQLType: string, isNonNullable: boolean, value: any): IBaseArgument => {
        return { name, graphQLType, isNonNullable, value };
    }

    private buildNested = (fieldName: string, responseField: IResponseField, fragments: { [key: string]: string }): string => {
        if (responseField.arguments && responseField.arguments.length) {
            return `${fieldName} (${generateVariableParams(responseField.arguments)}) ${responseField.responseBuilder?.build(fragments)}`;
        }

        return `${fieldName} ${responseField.responseBuilder?.build(fragments)}`;
    }

    public hasFields = () => !!Object.keys(this.fields).length;

    public isFragment = (fragments: { [key: string]: string }) => !!fragments[this.hashCode];

    public getArguments = (args: IBaseArgument[]): IBaseArgument[] => {
        let variables: IBaseArgument[] = [...args];
        Object.keys(this.fields)
        .filter(f => this.fields[f].responseBuilder)
        .forEach((k) => {
            const args = this.fields[k].arguments;
            if (args && args.length) {
                variables = [...variables, ...args];
            }

            variables = this.fields[k].responseBuilder!.getArguments(variables);
        });

        return variables;
    }

    public getFragmentsHashIds = (hashCodes: string[]): { [key: string]: string } => {
        let fragments: { [key: string]: string } = {};
        const hashCodeExists = hashCodes.some(h => h === this.hashCode);
        if (hashCodeExists && !fragments[this.hashCode]) {
            fragments[this.hashCode] = this.hashCode;
        }
        if (!hashCodeExists && Object.keys(this.fields).length) hashCodes.push(this.hashCode);
        Object.keys(this.fields)
            .forEach((k) => {
                if (this.fields[k].responseBuilder) {
                    fragments = { ...fragments, ...this.fields[k].responseBuilder?.getFragmentsHashIds(hashCodes) };
                }
            });

        return fragments;
    }

    public getFragmentDefinitions = (fragments: { [key: string]: string }) => {
        if (fragments[this.hashCode]) {
            fragments[this.hashCode] = `fragment Frag${this.hashCode} on ${this.typeName} { ${Object.keys(this.fields)
                .filter(k => !this.fields[k].responseBuilder || (this.fields[k].responseBuilder && !this.fields[k].responseBuilder?.isFragment(fragments)))
                .map(k => this.fields[k].responseBuilder ? this.buildNested(k, this.fields[k], fragments) : k)
                .join(', ')} }`;
        }

        Object.keys(this.fields)
            .forEach((k) => {
                if (this.fields[k].responseBuilder) {
                    this.fields[k].responseBuilder?.getFragmentDefinitions(fragments);
                }
            });
    }

    public build = (fragments: { [key: string]: string }): string => {
        if (fragments[this.hashCode]) {
            const nestedFields = Object.keys(this.fields)
                .filter(k => this.fields[k].responseBuilder && this.fields[k].responseBuilder?.isFragment(fragments))
                .map(k => this.buildNested(k, this.fields[k], fragments))
                .join(', ');
            return `{ ...Frag${this.hashCode}${nestedFields ? `, ${nestedFields}` : ''} }`;
        }

        return `{ ${Object.keys(this.fields)
            .filter(k => !this.fields[k].responseBuilder || this.fields[k].responseBuilder?.hasFields())
            .map(k => this.fields[k].responseBuilder ? this.buildNested(k, this.fields[k], fragments) : k)
            .join(', ')} }`;
    }
}
